import React, { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck, registerObject } from "go-form";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FormDateRangePicker } from "go-form/components/FormDateRangePicker";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	JpkExportBodyApi,
	JpkExportFileType,
	StockApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { getJpkDateRangeDefaultValue } from "../utils";

interface Props {
	form: UseFormReturn<JpkExportBodyApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<any>[]>>;
}

const JpkExportBasicInfoForm = ({ form, setErrors }: Props) => {
	const { t } = useTranslation();
	const exportFileTypeOptions = [
		{
			id: JpkExportFileType.JPK_MAG,
			label: t("enums.jpk_export_file.type.JPK_MAG"),
		},
		{
			id: JpkExportFileType.JPK_FA,
			label: t("enums.jpk_export_file.type.JPK_FA"),
		},
	];
	const {
		formState: { errors },
		register,
		setValue,
		control,
		watch,
	} = form;
	const [exportFileType, setExportFileType] = useState<JpkExportFileType>(exportFileTypeOptions[0].id);
	const isMobile = useWindowSize().isMobile;

	const searchStocks = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getStockSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const onChangeStock = (stock: StockApi) => {
		registerObject(register, `stock`, ["name", "id"]);
		setValue("stock", { label: stock.name, id: stock.id });
	};

	const stockIdValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const handleChangeExportFileType = (type: JpkExportFileType) => {
		setValue("with_additional_data", false, { shouldDirty: false });
		setExportFileType(type);
	};

	return (
		<>
			<h5 className={isMobile ? "mt-2" : "mt-0"}>{t("lib:common.word.basic_data")}</h5>
			<fieldset className="form-group">
				<div className="row">
					<div className="col-md-6">
						<FormSelectGroup
							control={control}
							options={exportFileTypeOptions}
							label={t("modules.jpk_export.field.file_type.title")}
							onChange={handleChangeExportFileType}
							name="file_type"
							isClearable={false}
							getOptionValue={(opt) => opt.id}
							defaultValue={{
								label: t(`enums.jpk_export_file.type.${exportFileType}`),
								id: exportFileType,
							}}
							errors={[]}
							data-testid="file_type"
						/>
					</div>
					<div className="col-md-6">
						<FormSelectGroup
							control={control}
							loadOptions={searchStocks}
							label={t("common.word.stock")}
							onChange={(_, stock) => onChangeStock(stock)}
							name="stock_id"
							isClearable={false}
							getOptionValue={(opt) => opt.id}
							defaultValue={{
								label: watch("stock")?.label,
								id: watch("stock_id"),
							}}
							errors={errors}
							customValidationConfig={stockIdValidationConfig}
							data-testid="stock_id"
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6 date-range-input-container">
						<FormDateRangePicker
							errors={errors}
							control={control}
							name="date_range"
							defaultValue={getJpkDateRangeDefaultValue()}
							label={t("modules.jpk_export.field.date_range.title")}
						/>
					</div>
				</div>
				{exportFileType === JpkExportFileType.JPK_FA && (
					<div className="row">
						<div className="col-md-6">
							<FormCheck
								name="with_additional_data"
								errors={[]}
								register={register}
								label={t("modules.jpk_export.field.fa_with_additional_data.title")}
							/>
						</div>
					</div>
				)}
			</fieldset>
		</>
	);
};

export default JpkExportBasicInfoForm;
